import { Fragment } from 'react';
import { CurrentUserFieldsFragment } from 'src/gqlReactTypings.generated.d';
import { FeatureFlag } from 'src/shared/design-system/FeatureFlag';
import { FaBadgeDollar } from 'src/shared/design-system/FontAwesome';
import {
  BecomeAShef,
  Bowl,
  Chat,
  Document,
  Follow,
  Gift,
  History,
  InfoCircle,
  LocationOutline,
  Lock,
  ManageMealPlan,
  Notification,
  Payment,
  Profile,
  QuestionMark,
  Star,
} from 'src/shared/design-system/Icon';
import { Routes } from 'src/shared/Routes';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import { Colors, FontSize, Spacing } from 'web-common/src/shared/styles';
import { getShefLinkType } from '../../header-v2/utils';
import { MenuItem } from './MenuItem';
import { ShefMenuItem } from './ShefMenuItem';

const UnreadIndicator = styled.span`
  background: ${Colors.RADISH};
  color: ${Colors.WHITE};
  text-align: center;
  border-radius: 50%;
  font-size: ${FontSize.XS};
  margin-left: ${Spacing.HALF};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

export enum MenuItemType {
  ADDRESSES = 'addresses',
  ADMIN = 'admin',
  APP_TOOLS = 'app-developer-tools',
  BECOME_A_SHEF = 'become-a-shef',
  FOLLOWING = 'following',
  GIFT = 'gift-card',
  HELP_CENTER = 'help-center',
  MANAGE_MEAL_PLAN = 'manage-meal-plan',
  MESSAGES = 'messages',
  NOTIFICATIONS = 'notifications',
  ORDERS = 'orders',
  PAYMENTS = 'payment',
  PRIVACY = 'privacy-policy',
  PROFILE = 'profile',
  REFERRAL = 'referral-share',
  SHEF = 'shef',
  STORY = 'our-story',
  TOS = 'terms-of-service',
  ZIPCODE = 'zipcode',
}

export const TOP_SECTION_MENU_ITEMS: MenuItemType[] = [
  MenuItemType.ADMIN,
  MenuItemType.MANAGE_MEAL_PLAN,
  MenuItemType.SHEF,
  MenuItemType.PROFILE,
  MenuItemType.ORDERS,
  MenuItemType.PAYMENTS,
  MenuItemType.ADDRESSES,
  MenuItemType.ZIPCODE,
];
export const MIDDLE_SECTION_MENU_ITEMS: MenuItemType[] = [
  MenuItemType.MESSAGES,
  MenuItemType.NOTIFICATIONS,
  MenuItemType.FOLLOWING,
  MenuItemType.REFERRAL,
  MenuItemType.HELP_CENTER,
];

export const LOGGED_IN_LOWER_SECTION_MENU_ITEMS: MenuItemType[] = [
  MenuItemType.STORY,
  MenuItemType.BECOME_A_SHEF,
  MenuItemType.GIFT,
];

export const LOGGED_OUT_LOWER_SECTION_MENU_ITEMS: MenuItemType[] = [
  ...LOGGED_IN_LOWER_SECTION_MENU_ITEMS,
  MenuItemType.ZIPCODE,
  MenuItemType.HELP_CENTER,
];

export const NATIVE_APP_LINKS: MenuItemType[] = [MenuItemType.PRIVACY, MenuItemType.TOS, MenuItemType.APP_TOOLS];

export const getMenuItems = (
  menuItems: MenuItemType[],
  currentUser: CurrentUserFieldsFragment | null,
  hide: () => void,
  unread: number,
  showZipModal: () => void,
  cachedZipCode: string | null,
  showManageMealPlans: boolean,
  userHasActiveMealPlan: boolean
) => {
  const shouldShowBecomeAShef = getShefLinkType(currentUser) === 'become-a-shef';

  return (
    <Fragment>
      {menuItems.map((menuItem) => {
        const item = match(menuItem)
          .with(MenuItemType.ADDRESSES, () => (
            <MenuItem
              name='delivery-address'
              icon={<LocationOutline width='24px' height='24px' />}
              onClick={hide}
              text='Delivery address'
              href={Routes.CONSUMER_ACCOUNT_DELIVERY_ADDRESSES}
              type='link'
            />
          ))
          .with(MenuItemType.ADMIN, () => {
            if (currentUser?.admin) {
              return (
                <MenuItem
                  name='admin'
                  icon={<Star width='24px' height='24px' outline />}
                  onClick={hide}
                  text='Admin'
                  href={Routes.ADMIN}
                  type='link'
                />
              );
            }

            return null;
          })
          .with(MenuItemType.APP_TOOLS, () => {
            if (currentUser?.admin) {
              return (
                <MenuItem
                  name='app-developer-tools'
                  icon={<InfoCircle width='24px' height='24px' />}
                  text='Developer Tools'
                  type='app'
                  screen='developerTools'
                />
              );
            }

            return null;
          })
          .with(MenuItemType.MANAGE_MEAL_PLAN, () =>
            showManageMealPlans && userHasActiveMealPlan ? (
              <MenuItem
                name='manage-meal-plan'
                icon={<ManageMealPlan />}
                onClick={hide}
                text='Manage weekly service'
                href={Routes.CONSUMER_MEAL_PLANS_MANAGE}
                type='link'
              />
            ) : null
          )
          .with(MenuItemType.BECOME_A_SHEF, () =>
            shouldShowBecomeAShef ? (
              <MenuItem
                name='become-a-shef'
                icon={<BecomeAShef height='24px' width='24px' outline />}
                onClick={hide}
                text='Become a shef'
                href={Routes.CONSUMER_BECOME_A_SHEF}
                type='link'
              />
            ) : null
          )
          .with(MenuItemType.FOLLOWING, () => (
            <MenuItem
              name='following'
              icon={<Follow width='24px' height='24px' outline />}
              onClick={hide}
              text='Following'
              href={Routes.CONSUMER_FOLLOWING}
              type='link'
            />
          ))
          .with(MenuItemType.GIFT, () => (
            <MenuItem
              name='gift-card'
              icon={<Gift width='24px' height='24px' outline />}
              onClick={hide}
              text='Gift cards'
              href={Routes.CONSUMER_GIFT_CARD}
              type='link'
            />
          ))
          .with(MenuItemType.HELP_CENTER, () => (
            <MenuItem
              name='help-center'
              icon={<QuestionMark width='20px' height='20px' />}
              onClick={hide}
              text='Customer Support'
              href={Routes.HELP_FAQ_PATH}
              type='externalLink'
            />
          ))
          .with(MenuItemType.MESSAGES, () => (
            <FeatureFlag name='chatEnabled'>
              <MenuItem
                name='messages'
                icon={<Chat width='24px' height='24px' />}
                onClick={hide}
                text={<>Messages{unread > 0 && <UnreadIndicator>{unread}</UnreadIndicator>}</>}
                href={Routes.CONSUMER_ACCOUNT_MESSAGES}
                type='link'
              />
            </FeatureFlag>
          ))
          .with(MenuItemType.NOTIFICATIONS, () => (
            <MenuItem
              name='notifications'
              icon={<Notification width='24px' height='24px' outline />}
              text='Notifications'
              type='link'
              href={Routes.CONSUMER_ACCOUNT_NOTIFICATIONS}
              onClick={hide}
            />
          ))
          .with(MenuItemType.ORDERS, () => (
            <MenuItem
              name='orders'
              icon={<History width='24px' height='24px' />}
              onClick={hide}
              text='Orders'
              href={Routes.CONSUMER_ACCOUNT_ORDERS}
              type='link'
            />
          ))
          .with(MenuItemType.PAYMENTS, () => (
            <MenuItem
              name='payment-method'
              icon={<Payment width='24px' height='24px' outline />}
              onClick={hide}
              text='Payment method'
              href={Routes.CONSUMER_ACCOUNT_PAYMENT_METHODS}
              type='link'
            />
          ))
          .with(MenuItemType.PRIVACY, () => (
            <MenuItem
              name='privacy-policy'
              icon={<Lock width='24px' height='24px' />}
              onClick={hide}
              text='Privacy Policy'
              href={Routes.CONSUMER_PRIVACY_POLICY}
              type='link'
            />
          ))
          .with(MenuItemType.PROFILE, () => (
            <MenuItem
              name='profile'
              icon={<Profile width='24px' height='24px' outline />}
              onClick={hide}
              text='Profile'
              href={Routes.CONSUMER_ACCOUNT_PROFILE}
              type='link'
            />
          ))
          .with(MenuItemType.REFERRAL, () => (
            <MenuItem
              name='get-referral-code'
              icon={<FaBadgeDollar />}
              onClick={hide}
              text={'Referrals'}
              href={Routes.CONSUMER_ACCOUNT_SHARE}
              type='link'
            />
          ))
          .with(MenuItemType.SHEF, () => <ShefMenuItem currentUser={currentUser} onClick={hide} />)
          .with(MenuItemType.STORY, () => (
            <MenuItem
              name='our-story'
              href={Routes.OUR_STORY}
              icon={<Bowl height='24px' width='24px' />}
              text='Our story'
              type='externalLink'
            />
          ))
          .with(MenuItemType.TOS, () => (
            <MenuItem
              name='terms-of-service'
              icon={<Document width='24px' height='24px' outline />}
              onClick={hide}
              text='Terms of Service'
              href={Routes.CONSUMER_TOS}
              type='link'
            />
          ))
          .with(MenuItemType.ZIPCODE, () => (
            <MenuItem
              name='browse-by-zip'
              icon={<LocationOutline width='24px' height='24px' />}
              onClick={showZipModal}
              text={`${cachedZipCode ? 'Update' : 'Add'} zip code${cachedZipCode ? `: ${cachedZipCode}` : ''}`}
              type='button'
            />
          ))
          .exhaustive();

        return <Fragment key={menuItem}>{item}</Fragment>;
      })}
    </Fragment>
  );
};
